import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Grid,
  TextField,
  InputAdornment,
  IconButton,
  Typography,
  Card,
  CardContent,
  CircularProgress
} from '@mui/material';
import { Search as SearchIcon, Clear as ClearIcon, Add as AddIcon } from '@mui/icons-material';

import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/i18n/types/i18nNamespace';
import type { I18nScenarioEditorNs, I18nCommonNs, I18nPersonasNs, I18nCoreNs } from '@/i18n/dictionaries/interfaces';
import { getLanguageDisplayName } from '@/app/utils/languageUtils';

import { isUserSuperAdminSelector } from 'app/selectors/userSelectors';
import { usePersonaGetPersonas, useRolesGetRoles } from '@/api-gen/api';
import type { PersonaDto, PersonaUsage } from '@/api-gen/api.schemas';
import { PersonaUsage as PersonaUsageEnum } from '@/api-gen/api.schemas';

import PersonaCard from '@/app/components/PersonasManager/PersonaCard/PersonaCard';
import CreatePersonaDialog from '@/app/components/PersonasManager/CreatePersonaDialog/CreatePersonaDialog';
import useDebouncedValue from '@/app/hooks/useDebouncedValue';
import PersonaCardSkeleton from './PersonaCardSkeleton';

interface PersonaSelectionDialogProps {
  open: boolean;
  onClose: () => void;
  onPersonaSelect: (persona: PersonaDto, usages: PersonaUsage[], defaultRoleId?: number) => void;
  existingPersonaIds: number[];
  scenarioCompanyId?: number;
  scenarioLanguage?: string | null;
  isProcessingSelection?: boolean;
}

const PersonaSelectionDialog: React.FC<PersonaSelectionDialogProps> = React.memo(
  ({
    open,
    onClose,
    onPersonaSelect,
    existingPersonaIds,
    scenarioCompanyId,
    scenarioLanguage,
    isProcessingSelection = false
  }) => {
    const [translate] = useTranslation([I18nNamespace.ScenarioEditor]);
    const [translateCommon] = useTranslation([I18nNamespace.Common]);
    const [translatePersonas] = useTranslation([I18nNamespace.Personas]);
    const [translateCore] = useTranslation([I18nNamespace.Core]);

    const isSuperAdmin = useSelector(isUserSuperAdminSelector);

    // Dialog state
    const [searchText, setSearchText] = useState('');
    const debouncedSearchText = useDebouncedValue(searchText, 300);
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

    // Fetch personas
    const { data: personasResponse, isLoading, refetch } = usePersonaGetPersonas();

    // Fetch roles for the scenario language
    const { data: rolesResponse } = useRolesGetRoles(
      { langCode: scenarioLanguage || '' },
      { query: { enabled: !!scenarioLanguage, staleTime: 5 * 60 * 1000 } }
    );
    const roles = (rolesResponse as any)?.data || rolesResponse || [];

    // Preferred caller role name per language and normalization helpers
    const normalizeRoleName = (name?: string) => (name || '').trim().toLowerCase();
    const getPreferredCallerRoleName = (lang?: string | null): string | undefined => {
      if (!lang) return undefined;
      const code = lang.toLowerCase().split('-')[0];
      const callerRoleMap: Record<string, string> = {
        ar: 'caller',
        da: 'ringer',
        de: 'anrufer',
        en: 'caller',
        es: 'llamador',
        fr: 'appelant',
        is: 'hringjandi',
        it: 'chiamante',
        nl: 'beller',
        nb: 'innringer',
        nn: 'innringer',
        no: 'innringer',
        sv: 'inringare'
      };
      return callerRoleMap[code];
    };

    // Fallback to first role if no Instructor role found
    const firstRoleId: number | undefined = Array.isArray(roles) && roles.length > 0 ? roles[0].id : undefined;

    // Filter personas
    const availablePersonas = useMemo(() => {
      const personasData = personasResponse?.data || personasResponse;
      if (!personasData || !Array.isArray(personasData)) return [];

      let filteredPersonas = personasData.filter((persona) => persona.id && !existingPersonaIds.includes(persona.id));

      // Company filter based on scenario's company
      if (scenarioCompanyId) {
        filteredPersonas = filteredPersonas.filter(
          (persona) => persona.companies?.some((pc) => pc.companyId === scenarioCompanyId) || false
        );
      }

      // Language filter - only show personas matching scenario language
      if (scenarioLanguage) {
        filteredPersonas = filteredPersonas.filter((persona) => persona.languageCode === scenarioLanguage);
      }

      // Search text filter (using debounced search)
      if (debouncedSearchText) {
        filteredPersonas = filteredPersonas.filter((persona) =>
          persona.name?.toLowerCase().includes(debouncedSearchText.toLowerCase())
        );
      }

      return filteredPersonas;
    }, [personasResponse, existingPersonaIds, debouncedSearchText, scenarioCompanyId, scenarioLanguage]);

    const handlePersonaSelect = (persona: PersonaDto) => {
      if (isProcessingSelection) return;
      // If this is the first persona being added to the scenario, default it to Default usage
      const usages = existingPersonaIds.length === 0 ? [PersonaUsageEnum.Default] : [];
      const preferredName = getPreferredCallerRoleName(scenarioLanguage);
      const preferredRole = preferredName ? roles.find((role: any) => normalizeRoleName(role.name) === preferredName) : undefined;
      const defaultRoleId: number | undefined = preferredRole?.id ?? firstRoleId;
      onPersonaSelect(persona, usages, defaultRoleId);
      handleDialogClose();
    };

    const handleCreateNew = () => {
      setIsCreateDialogOpen(true);
    };

    const handleCreateDialogClose = () => {
      setIsCreateDialogOpen(false);
      // Don't refetch here as we only want to refetch on successful creation
    };

    const handlePersonaCreated = (createdPersona?: PersonaDto) => {
      refetch().then(() => {
        // If we have the created persona data, auto-select it
        if (createdPersona) {
          handlePersonaSelect(createdPersona);
        }
      });
      setIsCreateDialogOpen(false);
    };

    const handleDialogClose = () => {
      if (isProcessingSelection) return;
      setSearchText('');
      onClose();
    };

    const handleClearSearch = () => {
      setSearchText('');
    };

    const renderPersonaSelection = () => {
      if (isLoading) {
        return (
          <Grid container spacing={2}>
            {[...Array(6)].map((_, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <PersonaCardSkeleton />
              </Grid>
            ))}
          </Grid>
        );
      }

      if (availablePersonas.length === 0) {
        return (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="300px">
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {debouncedSearchText
                ? translatePersonas(nameof.full<I18nPersonasNs>((n) => n.noPersonasFound))
                : scenarioLanguage
                ? translate(
                    nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.noMatchingLanguagePersonas),
                    { scenarioLanguage: getLanguageDisplayName(scenarioLanguage, translateCore) }
                  )
                : translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.noAvailablePersonas))}
            </Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreateNew} sx={{ mt: 2 }}>
              {translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.createNewPersona))}
            </Button>
          </Box>
        );
      }

      return (
        <Grid container spacing={2}>
          {/* Create New Card */}
          <Grid item xs={12} sm={6} md={4} sx={{ position: 'relative', overflow: 'hidden' }}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                cursor: 'pointer',
                border: '2px dashed',
                borderColor: 'primary.main',
                '&:hover': {
                  bgcolor: 'action.hover'
                }
              }}
              onClick={handleCreateNew}
            >
              <CardContent
                sx={{
                  flexGrow: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <AddIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" align="center">
                  {translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.createNew))}
                </Typography>
                <Typography variant="body2" color="text.secondary" align="center">
                  {translate(
                    nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.addNewPersonaToLibrary)
                  )}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Existing Personas */}
          {availablePersonas.map((persona) => (
            <Grid item xs={12} sm={6} md={4} key={persona.id} sx={{ position: 'relative', overflow: 'hidden' }}>
              <PersonaCard persona={persona} onClick={() => handlePersonaSelect(persona)} />
            </Grid>
          ))}
        </Grid>
      );
    };

    return (
      <>
        <Dialog
          open={open}
          onClose={(_, __) => {
            if (isProcessingSelection) return;
            handleDialogClose();
          }}
          maxWidth="lg"
          fullWidth
          onClick={(e) => e.stopPropagation()}
          disableEscapeKeyDown={isProcessingSelection}
          PaperProps={{ sx: { position: 'relative' } }}
        >
          {isProcessingSelection && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                bgcolor: 'rgba(255, 255, 255, 0.6)',
                zIndex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <CircularProgress />
            </Box>
          )}
          <DialogTitle>
            <Typography variant="h5">
              {translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.addPersonaToScenario))}
            </Typography>
          </DialogTitle>

          <DialogContent sx={{ minHeight: '60vh' }} onClick={(e) => e.stopPropagation()}>
            <Box>
              {/* Language Filter Info */}
              {scenarioLanguage && (
                <Box mb={2} p={2} sx={{ bgcolor: 'info.light', borderRadius: 1 }}>
                  <Typography variant="body2" color="info.contrastText">
                    {translate(
                      nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.languageFilterInfo),
                      { scenarioLanguage: getLanguageDisplayName(scenarioLanguage, translateCore) }
                    )}
                  </Typography>
                </Box>
              )}

              {/* Search and Filters */}
              <Box display="flex" gap={2} mb={3} flexWrap="wrap">
                <TextField
                  variant="outlined"
                  size="small"
                  placeholder={translatePersonas(nameof.full<I18nPersonasNs>((n) => n.searchPlaceholder))}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  sx={{ minWidth: 300, flexGrow: 1 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    endAdornment: searchText && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={handleClearSearch}>
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Box>

              {renderPersonaSelection()}
            </Box>
          </DialogContent>

          <DialogActions>
            <Button onClick={handleDialogClose} disabled={isProcessingSelection}>
              {translateCommon(nameof.full<I18nCommonNs>((n) => n.buttonLabels.cancel))}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Create Persona Dialog */}
        <CreatePersonaDialog
          open={isCreateDialogOpen}
          onClose={handleCreateDialogClose}
          onRefresh={handlePersonaCreated}
          defaultLanguageCode={scenarioLanguage || undefined}
        />
      </>
    );
  }
);

PersonaSelectionDialog.displayName = 'PersonaSelectionDialog';

export default PersonaSelectionDialog;
